import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Menu,
  X,
  Phone,
  Mail,
  MessageCircle,
  Instagram,
  Youtube,
  Star,
  Linkedin,
  Facebook,
} from 'lucide-react';
import { useGoogleReviews } from '@/hooks/use-reviews';
import { useInstagram } from '@/hooks/use-instagram';
import ContactActions from './ContactActions';

const Layout = ({
  children,
  showNav = true,
  showGoogleReviews = false,
  toggleContent = () => {},
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const googleReviews = useGoogleReviews();
  const { posts: instagramPosts, loading: instagramLoading } = useInstagram();

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Gallery', path: '/gallery' },
    { name: 'Our Best Work', path: '/best-work' },
    { name: 'Experience', path: '/experience' },
    { name: 'About', path: '/about' },
    { name: 'Testimonials', path: '/testimonials' },
    { name: 'Contact', path: '/contact' },
    { name: 'Packages', path: '/packages' },
  ];

  const isActive = path => location.pathname === path;
  const isHome = location.pathname === '/';

  const gotoHome = () => {
    toggleContent(false);
  };

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <ContactActions />
      {/* Fixed Header */}
      {showNav && (
        <header
          className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white border-b border-gray-200 ${scrolled ? 'shadow-md' : ''}`}
        >
          <div className="relative mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:min-h-[80px]">
            {isHome ? (
              // Link to home with toggle
              <Link
                onClick={gotoHome}
                className={`flex items-center group transition-[left,transform] duration-500 ease-in-out motion-reduce:transition-none lg:absolute lg:left-0 lg:top-1/2 lg:-translate-y-1/2 ${
                  scrolled
                    ? 'lg:left-1/2 lg:-translate-x-1/2 lg:delay-200'
                    : 'lg:translate-x-0 lg:delay-0'
                }`}
              >
                <img
                  src="/frames-by-gaurav-logo.svg"
                  alt="Frames By Gaurav"
                  className="block h-10 w-auto max-w-[160px] object-contain sm:h-12"
                />
              </Link>
            ) : (
              <Link
                to="/"
                className={`flex items-center group transition-[left,transform] duration-500 ease-in-out motion-reduce:transition-none lg:absolute lg:left-0 lg:top-1/2 lg:-translate-y-1/2 ${
                  scrolled
                    ? 'lg:left-1/2 lg:-translate-x-1/2 lg:delay-200'
                    : 'lg:translate-x-0 lg:delay-0'
                }`}
              >
                <img
                  src="/frames-by-gaurav-logo.svg"
                  alt="Frames By Gaurav"
                  className="block h-10 w-auto max-w-[160px] object-contain sm:h-12"
                />
              </Link>
            )}

            {/* Desktop Navigation */}
            <nav
              className={`hidden items-center space-x-8 transition-[opacity,transform] duration-300 ease-in-out motion-reduce:transition-none lg:absolute lg:right-0 lg:top-1/2 lg:-translate-y-1/2 lg:flex ${
                scrolled
                  ? 'pointer-events-none translate-x-6 opacity-0 lg:delay-0'
                  : 'translate-x-0 opacity-100 lg:delay-200'
              }`}
            >
              {navItems.map(item => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() =>
                    window.scrollTo({ top: 0, behavior: 'smooth' })
                  }
                  className={`text-sm tracking-wider uppercase transition-colors hover-slide ${
                    isActive(item.path)
                      ? 'text-black font-medium'
                      : 'text-gray-600 hover:text-black'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              {/* Instagram Icon - Desktop */}
              <a
                href="https://www.instagram.com/_btsandbeyond_"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-800 transition-colors hover:text-black"
                aria-label="Instagram"
              >
                <Instagram
                  size={24}
                  className="transition-transform hover:scale-110"
                />
              </a>
            </nav>

            {/* Mobile - Instagram Icon + Menu Button */}
            <div
              className={`flex items-center gap-2 transition-[opacity,transform] duration-300 ease-in-out motion-reduce:transition-none sm:gap-4 lg:absolute lg:right-6 lg:top-1/2 lg:-translate-y-1/2 ${
                scrolled
                  ? 'lg:translate-x-0 lg:opacity-100 lg:delay-200'
                  : 'lg:pointer-events-none lg:translate-x-4 lg:opacity-0 lg:delay-0'
              }`}
            >
              {/* Instagram Icon - Mobile */}
              <a
                href="https://www.instagram.com/framesbygaurav"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-800 transition-colors hover:text-black"
                aria-label="Instagram"
              >
                <Instagram size={24} />
              </a>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="relative p-2 text-black transition-colors hover:text-gray-700"
                aria-label="Toggle menu"
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {/* Side Navigation Menu with Instagram Posts */}
          <div
            className={`fixed right-0 top-16 bottom-0 w-80 md:w-96 bg-white shadow-2xl z-40 transition-transform duration-500 ${
              isMenuOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            {/* Menu Content */}
            <div className="h-full overflow-y-auto">
              {/* Navigation Items */}
              <nav className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-bold mb-4 uppercase tracking-wider">
                  Menu
                </h3>
                {navItems.map(item => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => {
                      setIsMenuOpen(false);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className={`block py-3 text-base tracking-wide transition-colors ${
                      isActive(item.path)
                        ? 'text-black font-bold'
                        : 'text-gray-700 hover:text-black'
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
              </nav>

              {/* Instagram Posts Section */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold uppercase tracking-wider">
                    Recent Posts
                  </h3>
                  <a
                    href="https://www.instagram.com/_btsandbeyond_"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-600 hover:text-black transition-colors"
                    aria-label="Follow on Instagram"
                  >
                    <Instagram size={20} />
                  </a>
                </div>

                {/* Instagram Grid */}
                {instagramLoading ? (
                  <div className="grid grid-cols-3 gap-2">
                    {[1, 2, 3, 4, 5, 6].map(i => (
                      <div
                        key={i}
                        className="aspect-square bg-gray-200 animate-pulse"
                      />
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {instagramPosts.map(post => (
                      <a
                        key={post.id}
                        href={post.permalink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="aspect-square overflow-hidden hover:opacity-75 transition-opacity"
                        title={post.caption}
                      >
                        <img
                          src={post.media_url}
                          alt={post.caption || 'Instagram post'}
                          className="w-full h-full object-cover"
                        />
                      </a>
                    ))}
                  </div>
                )}

                <a
                  href="https://www.instagram.com/_btsandbeyond_"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block mt-4 text-center text-sm text-gray-600 hover:text-black transition-colors"
                >
                  View More on Instagram →
                </a>
              </div>
            </div>
          </div>
          {/* Overlay Background */}
          {isMenuOpen && (
            <button
              type="button"
              className="fixed inset-0 top-16 bg-black/50 z-30 transition-opacity duration-500 cursor-default"
              onClick={() => setIsMenuOpen(false)}
              onKeyDown={e => {
                if (e.key === 'Escape') {
                  setIsMenuOpen(false);
                }
              }}
              aria-label="Close menu"
              tabIndex={0}
            />
          )}
        </header>
      )}

      {/* Main Content */}
      <main className={`flex-grow ${showNav ? 'pt-16' : ''}`}>{children}</main>

      {/* Google Reviews Section */}
      {showGoogleReviews && (
        <section className="py-20 bg-gray-50">
          <div className="px-6 mx-auto max-w-7xl">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold tracking-tight md:text-5xl">
                Google Reviews
              </h2>
              <div className="flex items-center justify-center gap-2 mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    size={24}
                    className="text-yellow-400 fill-yellow-400"
                  />
                ))}
              </div>
              <p className="text-lg text-gray-600">
                4.9 out of 5 based on 150+ reviews
              </p>
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
              {googleReviews.map((review, index) => (
                <div
                  key={index}
                  className="p-6 transition-colors bg-white border-2 border-gray-200 hover:border-black"
                >
                  <div className="flex items-center gap-2 mb-3">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star
                        key={i}
                        size={16}
                        className="text-yellow-400 fill-yellow-400"
                      />
                    ))}
                  </div>
                  <p className="mb-4 text-sm italic text-gray-700">
                    &ldquo;{review.text}&rdquo;
                  </p>
                  <div className="pt-4 border-t border-gray-200">
                    <p className="text-sm font-semibold">{review.name}</p>
                    <p className="text-xs text-gray-500">{review.date}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Find Us At Section */}
      <section className="py-12 bg-white border-t border-gray-200">
        <div className="px-6 mx-auto text-center max-w-7xl">
          <h3 className="mb-6 text-2xl font-bold tracking-tight">FIND US AT</h3>
          <div className="flex flex-wrap items-center justify-center gap-6">
            <a
              href="https://www.youtube.com/@ajayrajproductionslko"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-800 transition-colors hover:text-black"
              aria-label="YouTube"
            >
              <Youtube
                size={36}
                className="transition-transform hover:scale-110"
              />
            </a>
            <a
              href="https://www.instagram.com/_btsandbeyond_"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-800 transition-colors hover:text-black"
              aria-label="Instagram"
            >
              <Instagram
                size={36}
                className="transition-transform hover:scale-110"
              />
            </a>
            <a
              href="https://www.linkedin.com/company/framesbygaurav"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-800 transition-colors hover:text-black"
              aria-label="LinkedIn"
            >
              <Linkedin
                size={36}
                className="transition-transform hover:scale-110"
              />
            </a>
            <a
              href="https://www.facebook.com/framesbygaurav"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-800 transition-colors hover:text-black"
              aria-label="Facebook"
            >
              <Facebook
                size={36}
                className="transition-transform hover:scale-110"
              />
            </a>
            <a
              href="https://www.justdial.com/framesbygaurav"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-gray-800 transition-colors hover:text-black"
              aria-label="Justdial"
            >
              <svg
                width="36"
                height="36"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="transition-transform hover:scale-110"
              >
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
              </svg>
              s
            </a>
          </div>
        </div>
      </section>

      {/* Fixed Footer */}
      <footer className="py-12 text-white bg-black">
        <div className="px-6 mx-auto max-w-7xl">
          <div className="grid grid-cols-1 gap-8 mb-8 text-center md:grid-cols-3 md:text-left">
            {/* Brand */}
            <div>
              <h3 className="mb-2 text-xl font-bold">FRAMES BY GAURAV</h3>
              <p className="text-sm text-gray-400">
                Capturing Moments, Creating Memories
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="mb-4 text-sm font-semibold tracking-wider uppercase">
                Quick Links
              </h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/gallery"
                    onClick={() =>
                      window.scrollTo({ top: 0, behavior: 'smooth' })
                    }
                    className="text-sm text-gray-400 transition-colors hover:text-white"
                  >
                    Gallery
                  </Link>
                </li>
                <li>
                  <Link
                    to="/best-work"
                    onClick={() =>
                      window.scrollTo({ top: 0, behavior: 'smooth' })
                    }
                    className="text-sm text-gray-400 transition-colors hover:text-white"
                  >
                    Our Best Work
                  </Link>
                </li>
                <li>
                  <Link
                    to="/packages"
                    onClick={() =>
                      window.scrollTo({ top: 0, behavior: 'smooth' })
                    }
                    className="text-sm text-gray-400 transition-colors hover:text-white"
                  >
                    Packages
                  </Link>
                </li>
                <li>
                  <Link
                    to="/experience"
                    onClick={() =>
                      window.scrollTo({ top: 0, behavior: 'smooth' })
                    }
                    className="text-sm text-gray-400 transition-colors hover:text-white"
                  >
                    Experience
                  </Link>
                </li>
                <li>
                  <Link
                    to="/about"
                    onClick={() =>
                      window.scrollTo({ top: 0, behavior: 'smooth' })
                    }
                    className="text-sm text-gray-400 transition-colors hover:text-white"
                  >
                    About
                  </Link>
                </li>
                <li>
                  <Link
                    to="/contact"
                    onClick={() =>
                      window.scrollTo({ top: 0, behavior: 'smooth' })
                    }
                    className="text-sm text-gray-400 transition-colors hover:text-white"
                  >
                    Contact
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="mb-4 text-sm font-semibold tracking-wider uppercase">
                Get In Touch
              </h4>
              <ul className="space-y-3">
                <li>
                  <a
                    href="tel:+917398413465"
                    className="flex items-center justify-center text-sm text-gray-400 transition-colors md:justify-start hover:text-white group"
                  >
                    <Phone
                      size={16}
                      className="mr-2 transition-transform group-hover:scale-110"
                    />
                    +91 73984 13465
                  </a>
                </li>
                <li>
                  <a
                    href="mailto:info@framesbygaurav.com"
                    className="flex items-center justify-center text-sm text-gray-400 transition-colors md:justify-start hover:text-white group"
                  >
                    <Mail
                      size={16}
                      className="mr-2 transition-transform group-hover:scale-110"
                    />
                    info@framesbygaurav.com
                  </a>
                </li>
                <li>
                  <a
                    href="https://wa.me/917398413465"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center text-sm text-gray-400 transition-colors md:justify-start hover:text-white group"
                  >
                    <MessageCircle
                      size={16}
                      className="mr-2 transition-transform group-hover:scale-110"
                    />
                    WhatsApp
                  </a>
                </li>
                <li>
                  <a
                    href="https://maps.google.com/?q=Shop+No.+4+Sujanpura+Crossing+Alambagh+Lucknow"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start justify-center text-sm text-gray-400 transition-colors md:justify-start hover:text-white group"
                  >
                    <Mail
                      size={16}
                      className="mt-1 mr-2 transition-transform group-hover:scale-110"
                    />
                    <span className="text-left">
                      Shop No. 4, Sujanpura Crossing,
                      <br />
                      Alambagh, Lucknow,
                      <br />
                      UP-226005, India
                    </span>
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Copyright */}
          <div className="pt-8 text-center border-t border-gray-800">
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} Frames by Gaurav. All rights
              reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
